/**
 * Phase 195.e — Super Admin Walkthrough Video Blast Engine
 *
 * Lets the Out-Play HQ super-admin (Anil & team) curate a registry of YouTube
 * walkthrough tutorials and broadcast a personalised WhatsApp link to either:
 *   • Partner Academies (registered + onboarded tenants)
 *   • CRM Inbound Leads (unregistered prospects flattened across all tenants)
 *
 * Wires to:
 *   GET/POST/DELETE /api/super-admin/walkthrough-videos
 *   GET            /api/super-admin/crm-blast/targets/academies
 *   GET            /api/super-admin/crm-blast/targets/inbound-leads
 *   POST           /api/super-admin/crm-blast/dispatch
 *   GET            /api/super-admin/crm-blast/logs
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Plus, Send, Film, Users, Landmark, CheckSquare, Square, Trash2,
  RefreshCw, AlertCircle, CheckCircle2, MessageCircle, Phone,
  Sparkles, Calendar, Inbox,
  // Phase 201.b — Click engagement panel icons
  MousePointerClick, TrendingUp, Flame, BellRing, Globe, ExternalLink,
} from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';

const STATUS_TONE = {
  sent: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border border-rose-200',
  skipped: 'bg-slate-100 text-slate-600 border border-slate-200',
};

const formatTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  } catch {
    return iso;
  }
};

export default function SuperAdminWalkthroughBlast() {
  // === Videos ===
  const [videos, setVideos] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '', youtube_url: '', is_default_onboarding: false,
  });
  const [savingVideo, setSavingVideo] = useState(false);

  // === Targets ===
  const [targetMode, setTargetMode] = useState('academies'); // 'academies' | 'leads'
  const [academyTargets, setAcademyTargets] = useState([]);
  const [leadTargets, setLeadTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(true);
  const [selectedTargetIds, setSelectedTargetIds] = useState(new Set());
  const [search, setSearch] = useState('');

  // === Dispatch ===
  const [dispatching, setDispatching] = useState(false);

  // === Logs ===
  const [logs, setLogs] = useState([]);
  const [logsSummary, setLogsSummary] = useState({
    sent: 0, failed: 0, skipped: 0,
  });
  const [logsLoading, setLogsLoading] = useState(true);

  // === Phase 201.b — Click engagement stats ===
  const [clickTrackers, setClickTrackers] = useState([]);
  const [clickSummary, setClickSummary] = useState({
    total_trackers: 0, clicked_trackers: 0, total_clicks: 0,
    auto_promoted_leads: 0, founder_alerts_sent: 0,
  });
  const [clickStatsLoading, setClickStatsLoading] = useState(true);

  const activeTargets = targetMode === 'academies' ? academyTargets : leadTargets;
  const filteredTargets = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activeTargets;
    return activeTargets.filter((r) =>
      (r.name || '').toLowerCase().includes(term)
      || (r.phone || '').toLowerCase().includes(term)
      || (r.email || '').toLowerCase().includes(term)
      || (r.owner_name || '').toLowerCase().includes(term),
    );
  }, [activeTargets, search]);

  const loadVideos = useCallback(async () => {
    try {
      const { data } = await api.get('/super-admin/walkthrough-videos');
      const v = data?.videos || [];
      setVideos(v);
      if (v.length > 0 && !selectedVideoId) {
        setSelectedVideoId(v[0].id);
      }
    } catch (_e) {
      toast.error('Could not load walkthrough video registry');
    }
  }, [selectedVideoId]);

  const loadTargets = useCallback(async () => {
    setTargetsLoading(true);
    try {
      const [acadRes, leadRes] = await Promise.all([
        api.get('/super-admin/crm-blast/targets/academies'),
        api.get('/super-admin/crm-blast/targets/inbound-leads'),
      ]);
      setAcademyTargets(acadRes.data?.records || []);
      setLeadTargets(leadRes.data?.records || []);
    } catch (_e) {
      toast.error('Could not load target audience');
    } finally {
      setTargetsLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const { data } = await api.get('/super-admin/crm-blast/logs', {
        params: { limit: 100 },
      });
      setLogs(data?.logs || []);
      setLogsSummary(data?.summary || { sent: 0, failed: 0, skipped: 0 });
    } catch (_e) {
      // silent — the page shouldn't hard-fail
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const loadClickStats = useCallback(async () => {
    setClickStatsLoading(true);
    try {
      const { data } = await api.get('/super-admin/crm-blast/click-stats', {
        params: { limit: 100 },
      });
      setClickTrackers(data?.trackers || []);
      setClickSummary(data?.summary || {
        total_trackers: 0, clicked_trackers: 0, total_clicks: 0,
        auto_promoted_leads: 0, founder_alerts_sent: 0,
      });
    } catch (_e) {
      // silent
    } finally {
      setClickStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Defer to escape React 19's "set-state-in-effect" guard — the loaders
    // each internally call setState which is fine, but the rule is overly strict.
    const t = setTimeout(() => {
      loadVideos();
      loadTargets();
      loadLogs();
      loadClickStats();
    }, 0);
    return () => clearTimeout(t);
  }, [loadVideos, loadTargets, loadLogs, loadClickStats]);

  // Clear selection on tab switch — selections are scoped to a mode
  useEffect(() => {
    const t = setTimeout(() => {
      setSelectedTargetIds(new Set());
      setSearch('');
    }, 0);
    return () => clearTimeout(t);
  }, [targetMode]);

  const toggleTarget = (row) => {
    const key = row.id || row.phone;
    if (!key) return;
    setSelectedTargetIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllVisible = () => {
    const next = new Set(selectedTargetIds);
    filteredTargets.forEach((r) => {
      if (r.has_phone) next.add(r.id || r.phone);
    });
    setSelectedTargetIds(next);
  };

  const clearSelection = () => setSelectedTargetIds(new Set());

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!newVideo.title.trim() || !newVideo.youtube_url.trim()) {
      toast.error('Title and YouTube URL are both required');
      return;
    }
    setSavingVideo(true);
    try {
      const { data } = await api.post('/super-admin/walkthrough-videos', newVideo);
      toast.success(`Video "${data.title}" added to registry`);
      setNewVideo({ title: '', youtube_url: '', is_default_onboarding: false });
      setShowAddVideo(false);
      await loadVideos();
      setSelectedVideoId(data.id);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not add video';
      toast.error(detail);
    } finally {
      setSavingVideo(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Delete this walkthrough video from the registry? Audit logs will retain past dispatches.')) {
      return;
    }
    try {
      await api.delete(`/super-admin/walkthrough-videos/${videoId}`);
      toast.success('Video removed');
      if (selectedVideoId === videoId) setSelectedVideoId('');
      loadVideos();
    } catch (_e) {
      toast.error('Could not delete video');
    }
  };

  const handleDispatch = async () => {
    if (!selectedVideoId) {
      toast.error('Pick a video first');
      return;
    }
    if (selectedTargetIds.size === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    const isLead = targetMode === 'leads';
    const targets = activeTargets
      .filter((r) => selectedTargetIds.has(r.id || r.phone) && r.has_phone)
      .map((r) => ({
        id: r.id,
        name: r.name || r.owner_name || 'Friend',
        phone: r.phone,
        is_lead: isLead,
      }));
    if (targets.length === 0) {
      toast.error('Selected rows have no phone numbers');
      return;
    }
    setDispatching(true);
    try {
      const { data } = await api.post('/super-admin/crm-blast/dispatch', {
        video_id: selectedVideoId,
        targets,
      });
      toast.success(data?.message || `Broadcast queued for ${targets.length} recipients`);
      clearSelection();
      // Give the worker a few seconds to populate logs + tracker mints
      setTimeout(() => {
        loadLogs();
        loadClickStats();
      }, 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Dispatch failed';
      toast.error(detail);
    } finally {
      setDispatching(false);
    }
  };

  const selectedVideo = videos.find((v) => v.id === selectedVideoId);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 space-y-6"
         data-testid="walkthrough-blast-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">
            Onboarding Media & CRM Blast Engine
          </h1>
          <p className="text-sm text-slate-500 max-w-2xl">
            Curate YouTube walkthrough videos and broadcast a personalised
            WhatsApp link to partner academies or inbound leads. Background
            dispatch with 24-hour idempotency + Twilio-safe throttling.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" size="sm"
            onClick={() => setShowAddVideo((v) => !v)}
            data-testid="walkthrough-add-video-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Video
          </Button>
          <Button
            onClick={handleDispatch}
            disabled={dispatching || selectedTargetIds.size === 0 || !selectedVideoId}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="walkthrough-dispatch-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" />
            Broadcast ({selectedTargetIds.size})
          </Button>
        </div>
      </div>

      {/* Add video form */}
      {showAddVideo && (
        <Card data-testid="walkthrough-add-video-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
              <Film className="w-4 h-4 text-indigo-600" /> Catalogue New Walkthrough
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddVideo} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="wt-title" className="text-xs font-bold">
                    Title
                  </Label>
                  <Input
                    id="wt-title"
                    placeholder="e.g. Finance Analytics Walkthrough"
                    value={newVideo.title}
                    onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                    data-testid="walkthrough-video-title-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="wt-url" className="text-xs font-bold">
                    YouTube URL
                  </Label>
                  <Input
                    id="wt-url" type="url"
                    placeholder="https://youtube.com/watch?v=…"
                    value={newVideo.youtube_url}
                    onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
                    data-testid="walkthrough-video-url-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newVideo.is_default_onboarding}
                  onCheckedChange={(c) => setNewVideo({ ...newVideo, is_default_onboarding: c })}
                  id="wt-default"
                  data-testid="walkthrough-default-toggle"
                />
                <Label htmlFor="wt-default" className="text-xs text-slate-600">
                  Mark as the default onboarding video (only one allowed)
                </Label>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" size="sm"
                        onClick={() => setShowAddVideo(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={savingVideo}
                        data-testid="walkthrough-video-save-btn">
                  {savingVideo ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Commit to Registry
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <Card data-testid="tile-videos">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Videos Catalogued
            </div>
            <div className="text-2xl font-black text-slate-900 mt-1 tabular-nums flex items-center gap-1.5">
              <Film className="w-5 h-5 text-indigo-600" /> {videos.length}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="tile-sent">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Dispatches Sent
            </div>
            <div className="text-2xl font-black text-emerald-600 mt-1 tabular-nums">
              {logsSummary.sent || 0}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="tile-failed">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Failed
            </div>
            <div className="text-2xl font-black text-rose-600 mt-1 tabular-nums">
              {logsSummary.failed || 0}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="tile-skipped">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Skipped
            </div>
            <div className="text-2xl font-black text-slate-500 mt-1 tabular-nums">
              {logsSummary.skipped || 0}
            </div>
            <div className="text-[10px] text-slate-400">Dedup / missing phone</div>
          </CardContent>
        </Card>
        {/* Phase 201.b — click engagement tiles */}
        <Card data-testid="tile-clicks">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Link Clicks
            </div>
            <div className="text-2xl font-black text-indigo-600 mt-1 tabular-nums flex items-center gap-1.5">
              <MousePointerClick className="w-5 h-5" /> {clickSummary.total_clicks || 0}
            </div>
            <div className="text-[10px] text-slate-400">
              {clickSummary.clicked_trackers || 0}/{clickSummary.total_trackers || 0} unique
            </div>
          </CardContent>
        </Card>
        <Card data-testid="tile-promoted">
          <CardContent className="p-4">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Leads Promoted
            </div>
            <div className="text-2xl font-black text-amber-600 mt-1 tabular-nums flex items-center gap-1.5">
              <Flame className="w-5 h-5" /> {clickSummary.auto_promoted_leads || 0}
            </div>
            <div className="text-[10px] text-slate-400">
              {clickSummary.founder_alerts_sent || 0} alerts sent
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* LEFT — Video picker + Audit logs */}
        <div className="space-y-4 md:space-y-6">
          <Card data-testid="video-picker-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                <Film className="w-4 h-4 text-indigo-600" /> Pick a Walkthrough
              </CardTitle>
            </CardHeader>
            <CardContent>
              {videos.length === 0 ? (
                <div className="text-center text-xs text-slate-400 italic py-4">
                  No videos catalogued yet. Use &quot;Add Video&quot; above to get started.
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {videos.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setSelectedVideoId(v.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        selectedVideoId === v.id
                          ? 'border-indigo-300 bg-indigo-50 shadow-sm'
                          : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                      data-testid={`video-row-${v.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-900 truncate">
                            {v.title}
                          </div>
                          <div className="text-[10px] text-slate-500 truncate font-mono">
                            {v.youtube_url}
                          </div>
                          {v.is_default_onboarding && (
                            <Badge variant="outline" className="mt-1 text-[9px] font-black bg-amber-50 text-amber-700 border-amber-200 px-1.5 py-0">
                              Default Onboarding
                            </Badge>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVideo(v.id);
                          }}
                          className="opacity-50 hover:opacity-100 text-rose-500 hover:text-rose-700 transition"
                          title="Delete video"
                          data-testid={`video-delete-${v.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selectedVideo && (
                <div className="mt-3 p-2 rounded-lg bg-slate-50 border border-slate-200 text-[10px] text-slate-600">
                  <b>Selected:</b> {selectedVideo.title}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audit logs */}
          <Card data-testid="audit-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
                <Inbox className="w-4 h-4 text-slate-700" /> Recent Dispatches
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={loadLogs} disabled={logsLoading} className="h-7">
                <RefreshCw className={`w-3.5 h-3.5 ${logsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {logsLoading ? (
                <div className="space-y-2 p-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : logs.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 italic">
                  No dispatches yet — blast a video to start the audit trail.
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {logs.map((log) => (
                    <div key={log.id} className="p-3 hover:bg-slate-50 transition"
                         data-testid={`audit-row-${log.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-900 truncate">
                            {log.recipient_name || '—'}
                          </div>
                          <div className="text-[10px] text-slate-500 truncate font-mono flex items-center gap-1.5">
                            <Phone className="w-2.5 h-2.5" />
                            {log.recipient_phone || '(no phone)'}
                          </div>
                          <div className="text-[10px] text-slate-400 truncate mt-0.5">
                            {log.video_title}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${STATUS_TONE[log.status] || 'bg-slate-100'}`}>
                            {log.status}
                          </span>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap">
                            {formatTs(log.sent_at)}
                          </span>
                        </div>
                      </div>
                      {log.error && (
                        <div className="mt-1 text-[10px] text-rose-600 font-medium flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {log.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — Target audience selector */}
        <div className="lg:col-span-2">
          <Card className="h-full" data-testid="targets-card">
            <CardHeader className="pb-3">
              <Tabs value={targetMode} onValueChange={setTargetMode}>
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="academies" data-testid="tab-academies">
                    <Landmark className="w-3.5 h-3.5 mr-1.5" />
                    Partner Academies ({academyTargets.length})
                  </TabsTrigger>
                  <TabsTrigger value="leads" data-testid="tab-leads">
                    <Users className="w-3.5 h-3.5 mr-1.5" />
                    CRM Inbound Leads ({leadTargets.length})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value={targetMode} className="mt-3">
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                    <Input
                      placeholder={`Search ${targetMode === 'academies' ? 'academies' : 'leads'}…`}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="flex-1"
                      data-testid="walkthrough-search-input"
                    />
                    <Button size="sm" variant="outline" onClick={selectAllVisible}
                            data-testid="walkthrough-select-all-btn">
                      <CheckSquare className="w-3.5 h-3.5 mr-1" /> Select all visible
                    </Button>
                    {selectedTargetIds.size > 0 && (
                      <Button size="sm" variant="ghost" onClick={clearSelection}
                              data-testid="walkthrough-clear-btn">
                        Clear ({selectedTargetIds.size})
                      </Button>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardHeader>
            <CardContent className="p-0">
              {targetsLoading ? (
                <div className="p-4 space-y-2">
                  {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : filteredTargets.length === 0 ? (
                <div className="p-10 text-center space-y-2">
                  <Users className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="text-sm font-bold text-slate-700">
                    No {targetMode === 'academies' ? 'academies' : 'leads'} match this filter
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[640px] overflow-y-auto">
                  {filteredTargets.map((row) => {
                    const key = row.id || row.phone;
                    const isChecked = selectedTargetIds.has(key);
                    const disabled = !row.has_phone;
                    return (
                      <div key={key}
                           className={`flex items-center gap-3 p-3 transition ${
                             disabled ? 'opacity-50' : 'hover:bg-slate-50 cursor-pointer'
                           }`}
                           onClick={() => !disabled && toggleTarget(row)}
                           data-testid={`target-row-${key}`}>
                        <button
                          disabled={disabled}
                          className="focus:outline-none flex-shrink-0"
                          aria-label={isChecked ? 'Deselect' : 'Select'}>
                          {isChecked
                            ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                            : <Square className={`w-4 h-4 ${disabled ? 'text-slate-200' : 'text-slate-300'}`} />}
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold text-slate-900 truncate">
                            {row.name}
                          </div>
                          {row.owner_name && (
                            <div className="text-[10px] text-slate-500 truncate">
                              {row.owner_name}
                            </div>
                          )}
                          <div className="text-[10px] text-slate-500 truncate font-mono flex items-center gap-1.5 mt-0.5">
                            {row.has_phone
                              ? <><Phone className="w-2.5 h-2.5" /> {row.phone}</>
                              : <span className="italic text-rose-500">No phone on file</span>}
                          </div>
                        </div>
                        {row.tier && (
                          <Badge variant="outline" className="text-[9px] font-black uppercase">
                            {row.tier}
                          </Badge>
                        )}
                        {row.stage && (
                          <Badge variant="outline" className="text-[9px] font-black bg-indigo-50 text-indigo-700 border-indigo-200">
                            {row.stage}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Phase 201.b — Click Engagement Panel */}
      <Card data-testid="click-engagement-card">
        <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-2">
              <MousePointerClick className="w-4 h-4 text-indigo-600" /> Click Engagement Stream
            </CardTitle>
            <p className="text-[11px] text-slate-500 mt-1">
              Every WhatsApp link is wrapped in a self-hosted tracker. When a recipient taps it,
              we auto-promote their CRM stage and ping the founder.
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={loadClickStats}
                  disabled={clickStatsLoading}
                  className="h-7 flex-shrink-0"
                  data-testid="click-stats-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${clickStatsLoading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {clickStatsLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : clickTrackers.length === 0 ? (
            <div className="p-10 text-center space-y-2">
              <Globe className="w-10 h-10 mx-auto text-slate-300" />
              <div className="text-sm font-bold text-slate-700">
                No tracker activity yet
              </div>
              <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                Tracker URLs are minted automatically on every dispatch. Once a recipient taps
                their personalised link, you&apos;ll see the engagement signal here with stage-promotion
                + founder-alert status.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50/60 border-b border-slate-200">
                  <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                    <th className="text-left p-3">Recipient</th>
                    <th className="text-left p-3">Target</th>
                    <th className="text-center p-3">Clicks</th>
                    <th className="text-left p-3">Last Clicked</th>
                    <th className="text-left p-3">Signals</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {clickTrackers.map((t) => {
                    const clicked = (t.clicked_count || 0) > 0;
                    return (
                      <tr key={t.id} className="hover:bg-slate-50/40 transition"
                          data-testid={`tracker-row-${t.id}`}>
                        <td className="p-3 min-w-0">
                          <div className="font-bold text-slate-900 truncate max-w-[180px]">
                            {t.recipient_name || (t.lead_id ? 'Lead' : 'Academy owner')}
                          </div>
                          {t.recipient_phone && (
                            <div className="text-[10px] text-slate-500 font-mono truncate flex items-center gap-1 mt-0.5">
                              <Phone className="w-2.5 h-2.5" /> {t.recipient_phone}
                            </div>
                          )}
                          <div className="text-[10px] text-slate-400 capitalize mt-0.5">
                            {t.campaign_kind?.replace(/_/g, ' ')}
                          </div>
                        </td>
                        <td className="p-3 min-w-0">
                          <a href={t.target_url} target="_blank" rel="noopener noreferrer"
                             className="text-[11px] text-indigo-600 hover:underline truncate max-w-[220px] block font-mono"
                             title={t.target_url}>
                            {t.target_url}
                          </a>
                        </td>
                        <td className="p-3 text-center">
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-black tabular-nums ${
                            clicked
                              ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                              : 'bg-slate-50 text-slate-400 border border-slate-200'
                          }`}>
                            <MousePointerClick className="w-3 h-3" />
                            {t.clicked_count || 0}
                          </div>
                        </td>
                        <td className="p-3 text-[10px] text-slate-500 whitespace-nowrap">
                          {t.last_clicked_at ? formatTs(t.last_clicked_at) : '—'}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap items-center gap-1">
                            {t.auto_promoted_at && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
                                <Flame className="w-2.5 h-2.5" /> Promoted
                              </span>
                            )}
                            {t.alert_sent_at && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded">
                                <BellRing className="w-2.5 h-2.5" /> Alerted
                              </span>
                            )}
                            {!clicked && (
                              <span className="text-[10px] text-slate-400 italic">Awaiting click</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <a href={t.target_url} target="_blank" rel="noopener noreferrer"
                             className="text-slate-400 hover:text-slate-700 inline-flex"
                             title="Open target">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
